<template>
  <div class="overall-container">
    <filter-items
      v-model="params"
      :languages-list="languagesList"
      :qa-methods-list="qaMethodsList"
      :qa-models-list="qaModelsList"
      :report-type-list="reportTypeList"
      :text-category-list="textCategoryList"
      :industry-field-list="industryFieldList"
      :creation-date="creationDate"
      @date-change="$emit('date-change', $event)"
      title="Translation Quality KPIs"
      />
    <div class="graphics-relative-wrapper">
      <pre-loader :preloader-status="ajaxLoading" />
      <div class="graphics-container">
        <div class="text-center">
          <span class="date-box">{{ formatGraphsDate(creationDate[0]) }}</span>
          <span class="date-box">{{ formatGraphsDate(creationDate[1]) }}</span>
        </div>
        <div class="text-center">
          <span class="date-box">{{ formatGraphsDate(creationDateTrends[0]) }}</span>
          <span class="date-box">{{ formatGraphsDate(creationDateTrends[1]) }}</span>
        </div>
        <div></div>
        <charts-constructor
          :options="selected_time_options"
        />
        <charts-constructor
          :options="comparative_period_options"
        />
        <charts-constructor
          v-if="comparative_trend_options !== null"
          :options="comparative_trend_options"
        />
        <div class="stats-container">
          <div>
            <stat-item
              :value="selected_time.reports">
              reports
            </stat-item>
            <stat-item
              :value="selected_time.words">
              words
            </stat-item>
            <stat-item
              :value="selected_time.error_items">
              error items
            </stat-item>
            <stat-item
              :value="selected_time.error_points">
              error points
            </stat-item>
          </div>
          <div>
            <stat-item
              :value="selected_time.qa_level"
              color="#0576b2">
              QA level (average)
            </stat-item>
            <stat-item
              :value="selected_time.qa_score">
              QA level (average)
            </stat-item>
          </div>
        </div>
        <div class="stats-container">
          <div>
            <stat-item
              :value="comparative_period.reports">
              reports
            </stat-item>
            <stat-item
              :value="comparative_period.words">
              words
            </stat-item>
            <stat-item
              :value="comparative_period.error_items">
              error items
            </stat-item>
            <stat-item
              :value="comparative_period.error_points">
              error points
            </stat-item>
          </div>
          <div>
            <stat-item
              :value="comparative_period.qa_level"
              color="#0576b2">
              QA level (average)
            </stat-item>
            <stat-item
              :value="comparative_period.qa_score">
              QA level (average)
            </stat-item>
          </div>
        </div>
        <div class="stats-container" v-if="comparative_trend !== null">
          <div>
            <stat-item
              :value="comparative_trend.reports"
              percent>
              reports
            </stat-item>
            <stat-item
              :value="comparative_trend.words"
              percent>
              words
            </stat-item>
            <stat-item
              :value="comparative_trend.error_items"
              percent>
              error items
            </stat-item>
            <stat-item
              :value="comparative_trend.error_points"
              percent>
              error points
            </stat-item>
          </div>
          <div>
            <stat-item
              :value="comparative_trend.qa_level"
              percent
              color="#0576b2">
              QA level (average)
            </stat-item>
            <stat-item
              :value="comparative_trend.qa_score"
              percent>
              QA level (average)
            </stat-item>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import props from '../props.js';
import moment from 'moment';

export default Vue.extend({
  props: {
    ...props,
  },
  components: {
    'filter-items': () => import('../filters.vue'),
    'charts-constructor': () => import('@/components/charts/chartConstructor/index.js'),
    'stat-item': () => import('../StatItem.vue'),
    'pre-loader': () => import('@/components/preloader/index.vue'),
  },
  data: () => ({
    ajaxLoading: true,
    selected_time: {
      qa_level: 0,
      qa_score: 0,
      reports: 0,
      words: 0,
      error_items: 0,
      error_points: 0,
    },
    comparative_period: {
      qa_level: 0,
      qa_score: 0,
      reports: 0,
      words: 0,
      error_items: 0,
      error_points: 0,
    },
  }),
  watch: {
    creationDate() {
      this.getData();
    },
    value: {
      deep: true,
      handler() {
        this.getData();
      },
    },
  },
  computed: {
    params: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    comparative_trend() {
      if (this.ajaxLoading) {
        return {
          qa_level: 0,
          qa_score: 0,
          reports: 0,
          words: 0,
          error_items: 0,
          error_points: 0,
        };
      }
      const output = Object();
      Object.keys(this.selected_time).forEach((key) => {
        output[key] = this.getPercentDiff(this.selected_time[key], this.comparative_period[key]);
      });
      return output;
    },
    selected_time_options() {
      return {
        chart: {
          type: 'solidgauge',
        },
        title: {
          text: 'Selected time period',
        },
        pane: {
          center: ['50%', '80%'],
          size: '140%',
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: this.selected_time.qa_score === this.selected_time.qa_level ? '#fbba00'
              : (this.selected_time.qa_score > this.selected_time.qa_level ? '#08a546' : '#e51818'),
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
          },
        },
        plotOptions: {
          gauge: {
            dial: {
              rearLength: '0%',
              baseWidth: 3,
              topWidth: 1,
              baseLength: '95%',
            },
            dataLabels: {
              enabled: false,
            },
            pivot: {
              radius: 0,
            },
          },
        },
        yAxis: {
          min: 0,
          max: 100,
          labels: {
            formatter: () => '',
          },
          minorTickColor: '#000000',
          minorTickLength: 5,
          tickColor: '#000000',
          tickLength: 5,
        },
        series: [{
          name: 'QA level',
          type: 'gauge',
          data: [this.selected_time.qa_level],
          dial: {
            radius: '60%',
            backgroundColor: '#0075b1',
          },
        }, {
          name: 'QA score',
          type: 'gauge',
          data: [this.selected_time.qa_score],
          dial: {
            radius: '95%',
            backgroundColor: 'black',
          },
        }],
      };
    },
    comparative_period_options() {
      return {
        chart: {
          type: 'solidgauge',
        },
        title: {
          text: 'Comparative period',
        },
        pane: {
          center: ['50%', '80%'],
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: this.comparative_period.qa_score === this.comparative_period.qa_level ? '#fbba00'
              : (this.comparative_period.qa_score > this.comparative_period.qa_level ? '#08a546' : '#e51818'),
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
          },
        },
        plotOptions: {
          gauge: {
            dial: {
              rearLength: '0%',
              baseWidth: 3,
              topWidth: 1,
              baseLength: '95%',
            },
            dataLabels: {
              enabled: false,
            },
            pivot: {
              radius: 0,
            },
          },
        },
        yAxis: {
          min: 0,
          max: 100,
          labels: {
            formatter: () => '',
          },
          minorTickColor: '#000000',
          minorTickLength: 5,
          tickColor: '#000000',
          tickLength: 5,
        },
        series: [{
          name: 'QA level',
          type: 'gauge',
          data: [this.comparative_period.qa_level],
          dial: {
            radius: '60%',
            backgroundColor: '#0075b1',
          },
        }, {
          name: 'QA score',
          type: 'gauge',
          data: [this.comparative_period.qa_score],
          dial: {
            radius: '95%',
            backgroundColor: 'black',
          },
        }],
      };
    },
    comparative_trend_options() {
      const maxValue = this.comparative_trend.qa_level > 50 ? (() => {
        let i = 50;
        do {
          i += 5;
        } while (i > this.comparative_trend.qa_level);
        return i;
      })() : 50;
      return {
        chart: {
          type: 'solidgauge',
        },
        title: {
          text: 'Comparative period',
        },
        pane: {
          center: ['50%', '80%'],
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: {
              linearGradient: {
                x1: 0, x2: 1, y1: 0, y2: 0,
              },
              stops: [
                [0, '#e51818'],
                [0.5, '#e51818'],
                [0.5, '#08a546'],
                [1, '#08a546'],
              ],
            },
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
          },
        },
        tooltip: {
          formatter: function() {
            return `${this.series.name}: ${this.y}%`;
          },
        },
        plotOptions: {
          gauge: {
            dial: {
              rearLength: '0%',
              baseWidth: 3,
              topWidth: 1,
              baseLength: '95%',
            },
            dataLabels: {
              enabled: false,
            },
            pivot: {
              radius: 0,
            },
          },
        },
        yAxis: {
          min: -maxValue,
          max: +maxValue,
          labels: {
            format: '{value}%',
            distance: 20,
            style: {
              color: '#000000',
            },
          },
          minorTickColor: '#000000',
          minorTickLength: 5,
          tickColor: '#000000',
          tickLength: 5,
        },
        series: [{
          name: 'QA level',
          type: 'gauge',
          data: [this.comparative_trend.qa_level],
          dial: {
            radius: '60%',
            backgroundColor: '#0075b1',
          },
        }, {
          name: 'QA score',
          type: 'gauge',
          data: [this.comparative_trend.qa_score],
          dial: {
            radius: '95%',
            backgroundColor: 'black',
          },
        }],
      };
    },
  },
  methods: {
    getPercentDiff(a, b) {
      const [max, min, multiplier] = a >= b ? [a, b, 1] : [b, a, -1];
      return Math.round(((max / min) - 1) * 100) * multiplier;
    },
    formatGraphsDate(val) {
      return moment.utc(val, 'MMM YY').format('MMMM YYYY');
    },
    getData() {
      this.ajaxLoading = true;
      Promise.all([
        new Promise((res) => setTimeout(res, 2000)).then(() => {
          this.selected_time = {
            qa_level: 68,
            qa_score: 75,
            reports: 30,
            words: 17346,
            error_items: 58,
            error_points: 2670,
          };
        }),
        new Promise((res) => setTimeout(res, 3000)).then(() => {
          this.comparative_period = {
            qa_level: 66,
            qa_score: 66,
            reports: 28,
            words: 14591,
            error_items: 43,
            error_points: 2266,
          };
        }),
      ]).finally(() => {
        this.ajaxLoading = false;
      });
    },
  },
  mounted() {
    this.getData();
  },
});
</script>

<style lang="scss">
.overall-container {
  .graphics-relative-wrapper {
    position: relative;
  }
  .graphics-container {
    background: #fff;
    padding-top: 20px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: min-content 350px min-content;

    .date-box {
      display: inline-block;
      padding: 3px;
      border: 1px solid #666;
      margin: 3px 7px;
    }
  }
}
</style>
